import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import axios from "../../../helpers/axios";
import { generatePublicUrl } from "../../../urlConfig";
import { closeOfficeEntityModal } from "../../../actions/officeEntityModal.actions";
import { openOfficeTaskCreateModal } from "../../../actions/officeTaskCreateModal.actions";
import { openOfficeOrderModal } from "../../../actions/officeOrderModal.actions";
import { openOfficeProductGallery } from "../../../actions/officeProductGallery.actions";
import { openOfficeProduct3D } from "../../../actions/officeProduct3D.actions";

import "../../../scss/adminOffice/components/_global-entity-modal-AO.scss";

const TYPE_META = {
    product: { label: "Товар", icon: "▣" },
    task: { label: "Задание", icon: "✓" },
    member: { label: "Сотрудник", icon: "●" },
    sales_session: { label: "3D-сессия", icon: "◈" },
    order: { label: "Заказ", icon: "▤" },
    warehouse_material: { label: "Склад", icon: "▦" },
};

const TASK_STATUS = {
    published: "Доступно",
    accepted: "Принято",
    in_progress: "В работе",
    review: "На проверке",
    done: "Выполнено",
    rework: "На доработке",
    cancelled: "Отменено",
};

const TASK_PRIORITY = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

const SESSION_STATUS = {
    active: "Активна",
    closed: "Закрыта",
    converted_to_order: "Создан заказ",
    archived: "Архив",
};

const ROLE_LABELS = {
    owner: "Владелец",
    admin: "Администратор",
    manager: "Менеджер",
    sales: "Продажи",
    installer: "Монтажник",
    driver: "Водитель",
    warehouse: "Склад",
    accountant: "Бухгалтер",
    worker: "Работник",
};

const getId = (value) => String(value?._id || value?.id || value || "");

const getUserName = (user = {}) => (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.email ||
    user.username ||
    "Пользователь"
);

const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatMoney = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return "—";
    return `${number.toLocaleString("ru-RU")} ₽`;
};

const UNIT_LABELS = {
    pcs: "шт.",
    m: "м",
    m2: "м²",
    m3: "м³",
    kg: "кг",
    t: "т",
    l: "л",
    bag: "меш.",
    bucket: "вед.",
    sheet: "лист",
    set: "компл.",
};

const MATERIAL_TYPE_LABELS = {
    raw: "Сырьё",
    consumable: "Расходник",
    service: "Услуга",
    component: "Компонент",
    packaging: "Упаковка",
    other: "Другое",
};

const formatQty = (value, unit) => {
    const number = Number(value || 0);
    const formatted = Number.isInteger(number)
        ? number.toLocaleString("ru-RU")
        : number.toLocaleString("ru-RU", { maximumFractionDigits: 3 });

    return `${formatted} ${UNIT_LABELS[unit] || unit || ""}`.trim();
};

const getImageSrc = (value) => {
    if (!value) return "";
    if (/^(https?:)?\/\//i.test(value) || value.startsWith("blob:") || value.startsWith("data:")) {
        return value;
    }
    return generatePublicUrl(value);
};

const Row = ({ label, value }) => (
    <div className="global-entity-modal-AO__row">
        <span>{label}</span>
        <strong>{value === undefined || value === null || value === "" ? "—" : value}</strong>
    </div>
);

function ProductPreview({ entity, details, onOpenGallery }) {
    const image = getImageSrc(entity?.image);
    const dimensions = [details?.height, details?.width, details?.length].filter(Boolean).join(" × ");

    return (
        <div className="global-entity-product-AO">
            <div className="global-entity-product-AO__hero">
                <div
                    className="global-entity-product-AO__image is-product-gallery"
                    onClick={onOpenGallery}
                    title="Открыть фотографии товара"
                >
                    {image ? (
                        <img
                            src={image}
                            alt={entity?.title || "Товар"}
                            loading="lazy"
                            decoding="async"
                            onError={(event) => {
                                event.currentTarget.style.display = "none";
                            }}
                        />
                    ) : (
                        <span>Нет фото</span>
                    )}

                    <i className="product-gallery-trigger-AO__zoom" aria-hidden="true">⌕</i>
                </div>

                <div className="global-entity-product-AO__main">
                    <div className="global-entity-product-AO__chips">
                        {details?.article !== null && details?.article !== undefined ? <span>Арт. {details.article}</span> : null}
                        {details?.categoryName ? <span>{details.categoryName}</span> : null}
                        {details?.hasModel ? <span>3D-модель</span> : null}
                    </div>

                    <h3>{entity?.title || "Товар"}</h3>
                    <p>{details?.description || entity?.subtitle || "Информация о товаре"}</p>
                </div>
            </div>

            <div className="global-entity-modal-AO__grid">
                <Row label="Материал" value={details?.materialName} />
                <Row label="Размер" value={dimensions || details?.size} />
                <Row label="Вес" value={details?.weight} />
                <Row label="Цена" value={details?.pricingMode === "linearMeter" ? `${formatMoney(details?.pricePerMeter)} / пог. м` : formatMoney(details?.price)} />
                <Row label="Тип цены" value={details?.pricingMode} />
                <Row label="Работа" value={details?.workLabel || details?.workType} />
                <Row label="Обновлён" value={formatDate(details?.updatedAt)} />
                <Row label="ID" value={entity?.id} />
            </div>
        </div>
    );
}

function WarehouseMaterialPreview({ entity, details }) {
    const unit = details?.unit || entity?.payload?.unit || "pcs";
    const dimensions = [
        details?.dimensions?.height,
        details?.dimensions?.width,
        details?.dimensions?.length,
    ].filter(Boolean).join(" × ");
    const warehouses = Array.isArray(details?.warehouses) ? details.warehouses : [];

    return (
        <div className="global-entity-warehouse-AO">
            <div className="global-entity-modal-AO__grid is-wide">
                <Row label="SKU" value={details?.sku} />
                <Row label="Тип" value={MATERIAL_TYPE_LABELS[details?.materialType] || details?.materialType} />
                <Row label="Единица" value={UNIT_LABELS[unit] || unit} />
                <Row label="Размер" value={dimensions || "—"} />
                <Row label="Выбранный склад" value={details?.warehouseName || "Все склады"} />
                <Row label="Остаток" value={formatQty(details?.qty, unit)} />
                <Row label="Резерв" value={formatQty(details?.reserved, unit)} />
                <Row label="Доступно" value={formatQty(details?.available, unit)} />
                <Row label="Минимум" value={formatQty(details?.minStock, unit)} />
                <Row label="Нужно докупить" value={formatQty(details?.needToBuy, unit)} />
                <Row label="Всего по офису" value={formatQty(details?.officeAvailable, unit)} />
                <Row label="Цена материала" value={formatMoney(details?.price)} />
            </div>

            {details?.description ? (
                <section className="global-entity-task-AO__section">
                    <h4>Описание</h4>
                    <div className="global-entity-task-AO__description">{details.description}</div>
                </section>
            ) : null}

            {warehouses.length ? (
                <section className="global-entity-task-AO__section">
                    <h4>Остатки по складам</h4>
                    <div className="global-entity-modal-AO__grid is-wide">
                        {warehouses.map((warehouse) => (
                            <Row
                                key={warehouse.warehouseId}
                                label={`${warehouse.warehouseName}${warehouse.isActive === false ? " · выключен" : ""}`}
                                value={`${formatQty(warehouse.available, unit)} доступно · ${formatQty(warehouse.reserved, unit)} резерв`}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
}

function TaskPreview({ entity, details }) {
    const assignees = Array.isArray(details?.assignees)
        ? details.assignees.map(getUserName).filter(Boolean)
        : [];

    if (!assignees.length && details?.claimedBy) {
        assignees.push(getUserName(details.claimedBy));
    }

    const checklist = Array.isArray(details?.checklist) ? details.checklist : [];

    return (
        <div className="global-entity-task-AO">
            <div className="global-entity-task-AO__chips">
                <span className={`is-status is-${details?.status || "published"}`}>
                    {TASK_STATUS[details?.status] || details?.status || "—"}
                </span>
                <span className={`is-priority is-${details?.priority || "normal"}`}>
                    {TASK_PRIORITY[details?.priority] || "Обычный"}
                </span>
            </div>

            {details?.description ? (
                <div className="global-entity-task-AO__description">{details.description}</div>
            ) : null}

            <div className="global-entity-modal-AO__grid">
                <Row label="Исполнитель" value={assignees.join(", ") || "Не назначено"} />
                <Row label="Срок" value={formatDate(details?.dueAt)} />
                <Row label="Создал" value={getUserName(details?.createdBy || {})} />
                <Row label="Выполнено" value={formatDate(details?.completedAt)} />
                <Row label="Фото" value={details?.photoCount || 0} />
                <Row label="Фотоотчёт" value={details?.reportPhotoCount || 0} />
            </div>

            {checklist.length ? (
                <section className="global-entity-task-AO__section">
                    <h4>Чек-лист</h4>
                    <div className="global-entity-task-AO__checklist">
                        {checklist.map((item) => (
                            <div key={getId(item)} className={item.completed ? "is-done" : ""}>
                                <i>{item.completed ? "✓" : "○"}</i>
                                <span>
                                    <strong>{item.text}</strong>
                                    {item.lastChangedAt || item.completedAt ? (
                                        <small>
                                            {item.lastChange === "reopened" ? "Снял отметку" : "Отметил"}{" "}
                                            {getUserName(item.lastChangedBy || item.completedBy || {})}
                                            {" · "}
                                            {formatDate(item.lastChangedAt || item.completedAt)}
                                        </small>
                                    ) : null}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
}

function MemberPreview({ entity, details }) {
    const image = getImageSrc(details?.profilePicture || entity?.image);

    return (
        <div className="global-entity-member-AO">
            <div className="global-entity-member-AO__avatar">
                {image ? <img src={image} alt={details?.name || entity?.title || "Сотрудник"} /> : <span>{String(details?.name || entity?.title || "С").slice(0, 1).toUpperCase()}</span>}
            </div>

            <div className="global-entity-member-AO__info">
                <h3>{details?.name || entity?.title || "Сотрудник"}</h3>
                <div>{ROLE_LABELS[details?.role] || details?.role || "Сотрудник"}</div>
            </div>

            <div className="global-entity-modal-AO__grid is-wide">
                <Row label="Email" value={details?.email} />
                <Row label="Логин" value={details?.username} />
                <Row label="Телефон" value={details?.contactNumber} />
                <Row label="Был в сети" value={formatDate(details?.lastSeenAt)} />
            </div>
        </div>
    );
}

function SessionPreview({ entity, details }) {
    return (
        <div className="global-entity-session-AO">
            <div className="global-entity-session-AO__hero">
                <div className="global-entity-session-AO__icon">◈</div>
                <div>
                    <span>{SESSION_STATUS[details?.status] || details?.status || "Сессия"}</span>
                    <h3>{details?.title || entity?.title || "3D-сессия"}</h3>
                    <p>{details?.sessionId || entity?.payload?.sessionId || ""}</p>
                </div>
            </div>

            <div className="global-entity-modal-AO__grid">
                <Row label="Код сессии" value={details?.sessionId} />
                <Row label="Статус" value={SESSION_STATUS[details?.status] || details?.status} />
                <Row label="Текущий раздел" value={details?.currentTab} />
                <Row label="Тип сцены" value={details?.sceneKind} />
                <Row label="Заказчик" value={details?.customerName} />
                <Row label="Активность" value={formatDate(details?.lastActivityAt)} />
                <Row label="Создана" value={formatDate(details?.createdAt)} />
                <Row label="ID" value={entity?.id} />
            </div>
        </div>
    );
}

export default function GlobalOfficeEntityModal() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const modal = useSelector((state) => state.officeEntityModal || {});

    const {
        open,
        type,
        entityId,
        officeId,
        sourceTitle,
        openedAt,
        payload,
    } = modal;

    const [portalTarget, setPortalTarget] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const typeMeta = TYPE_META[type] || { label: "Объект", icon: "◆" };

    useEffect(() => {
        if (typeof document === "undefined") return;
        setPortalTarget(document.body);
    }, []);

    useEffect(() => {
        if (!open || !officeId || !entityId || !type || type === "order") return undefined;

        let mounted = true;
        setLoading(true);
        setError("");
        setData(null);

        const warehouseContextId = type === "warehouse_material"
            ? String(payload?.payload?.warehouseId || "all")
            : "";

        axios
            .get(`/office/${officeId}/chat-entities/${type}/${entityId}/preview`, {
                params: type === "warehouse_material"
                    ? { warehouseId: warehouseContextId || "all" }
                    : undefined,
            })
            .then((response) => {
                if (!mounted) return;
                setData(response.data || null);
            })
            .catch((requestError) => {
                if (!mounted) return;
                setError(
                    requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Не удалось загрузить объект."
                );
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [
        open,
        officeId,
        entityId,
        type,
        openedAt,
        payload?.payload?.warehouseId,
    ]);

    useEffect(() => {
        if (!open || type !== "order" || !officeId || !entityId) return;

        dispatch(closeOfficeEntityModal());
        dispatch(openOfficeOrderModal({
            orderId: entityId,
            officeId,
            activeTab: "overview",
            source: "entity-router",
            sourceTitle: sourceTitle || "Глобальный просмотр заказа",
            payload: payload || null,
        }));
    }, [
        dispatch,
        open,
        type,
        officeId,
        entityId,
        sourceTitle,
        payload,
    ]);

    useEffect(() => {
        if (!open || typeof document === "undefined") return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                dispatch(closeOfficeEntityModal());
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [dispatch, open]);

    const entity = data?.entity || payload || null;
    const details = data?.details || {};
    const meta = data?.meta || {};

    const sectionLink = useMemo(() => {
        if (!officeId || !entityId) return "";

        if (type === "product") {
            return `/adminLayout?tab=products&office=${encodeURIComponent(officeId)}&product=${encodeURIComponent(entityId)}`;
        }

        if (type === "task") {
            return `/adminLayout?tab=tasks&office=${encodeURIComponent(officeId)}&task=${encodeURIComponent(entityId)}`;
        }

        if (type === "member") {
            return `/adminLayout?tab=employees&office=${encodeURIComponent(officeId)}&member=${encodeURIComponent(entityId)}`;
        }

        if (type === "warehouse_material") {
            const params = new URLSearchParams();
            params.set("tab", "warehouse");
            params.set("office", officeId);
            params.set("taskMaterial", entityId);

            const warehouseId = details?.warehouseId || entity?.payload?.warehouseId || "";
            if (warehouseId) params.set("taskWarehouse", warehouseId);

            return `/adminLayout?${params.toString()}`;
        }

        if (type === "sales_session") {
            const sessionId = details?.sessionId || entity?.payload?.sessionId || "";
            return `/adminLayout?tab=salesSessions&office=${encodeURIComponent(officeId)}&salesSession=${encodeURIComponent(sessionId)}`;
        }

        return "";
    }, [
        officeId,
        entityId,
        type,
        details?.sessionId,
        details?.warehouseId,
        entity?.payload?.sessionId,
        entity?.payload?.warehouseId,
    ]);

    const openProductGallery = () => {
        if (type !== "product" || !officeId || !entityId) return;

        dispatch(openOfficeProductGallery({
            officeId,
            productId: entityId,
            title: entity?.title || "Товар",
            thumbnail: entity?.image || "",
            source: "global-product-preview",
        }));
    };

    const openProduct3D = () => {
        if (
            type !== "product" ||
            !officeId ||
            !entityId ||
            details?.hasModel !== true ||
            meta?.canUseCalc3d !== true
        ) return;

        dispatch(openOfficeProduct3D({
            officeId,
            productId: entityId,
            title: entity?.title || "Товар",
            source: "global-product-preview",
            product: {
                ...(entity || {}),
                ...(details || {}),
                _id: entityId,
                id: entityId,
                fullName: entity?.title || "Товар",
                payload: entity?.payload || {},
            },
        }));
    };

    if (type === "order") return null;

    if (!open || !portalTarget) return null;

    const close = () => dispatch(closeOfficeEntityModal());

    const openSection = () => {
        if (!sectionLink) return;
        close();
        navigate(sectionLink);
    };

    const createTask = () => {
        if (!meta?.canCreateTask || !officeId || !entityId) return;

        close();

        dispatch(openOfficeTaskCreateModal({
            officeId,
            source: type,
            sourceTitle: `${typeMeta.label}: ${entity?.title || entityId}`,
            prefill: {
                links: type === "product"
                    ? {
                        productId: entityId,
                        productName: entity?.title || "",
                        article: entity?.payload?.article || "",
                    }
                    : type === "warehouse_material"
                        ? {
                            materialId: entityId,
                            warehouseId: details?.warehouseId || entity?.payload?.warehouseId || "",
                        }
                        : {},
            },
        }));
    };

    const open3DSession = () => {
        const sessionId = details?.sessionId || entity?.payload?.sessionId || "";
        if (!sessionId || !officeId) return;

        close();
        navigate(`/adminLayout?tab=calc3d&office=${encodeURIComponent(officeId)}&salesSession=${encodeURIComponent(sessionId)}`);
    };

    return createPortal(
        <div className="global-entity-modal-AO" role="dialog" aria-modal="true">
            <button
                type="button"
                className="global-entity-modal-AO__backdrop"
                onClick={close}
                aria-label="Закрыть"
            />

            <section className="global-entity-modal-AO__panel">
                <header className="global-entity-modal-AO__head">
                    <div className="global-entity-modal-AO__head-icon">{typeMeta.icon}</div>
                    <div className="global-entity-modal-AO__head-main">
                        <span>{sourceTitle || `Быстрый просмотр · ${typeMeta.label}`}</span>
                        <h2>{entity?.title || typeMeta.label}</h2>
                        {entity?.subtitle ? <p>{entity.subtitle}</p> : null}
                    </div>
                    <button type="button" className="global-entity-modal-AO__close" onClick={close}>×</button>
                </header>

                <div className="global-entity-modal-AO__body">
                    {loading ? (
                        <div className="global-entity-modal-AO__state">Загружаем данные...</div>
                    ) : error ? (
                        <div className="global-entity-modal-AO__state is-error">{error}</div>
                    ) : (
                        <>
                            {type === "product" ? <ProductPreview entity={entity} details={details} onOpenGallery={openProductGallery} /> : null}
                            {type === "warehouse_material" ? <WarehouseMaterialPreview entity={entity} details={details} /> : null}
                            {type === "task" ? <TaskPreview entity={entity} details={details} /> : null}
                            {type === "member" ? <MemberPreview entity={entity} details={details} /> : null}
                            {type === "sales_session" ? <SessionPreview entity={entity} details={details} /> : null}
                            {!['product', 'warehouse_material', 'task', 'member', 'sales_session'].includes(type) ? (
                                <div className="global-entity-modal-AO__state">Быстрый просмотр этого объекта пока недоступен.</div>
                            ) : null}
                        </>
                    )}
                </div>

                <footer className="global-entity-modal-AO__actions">
                    {!loading && !error && type === "product" && details?.hasModel && meta?.canUseCalc3d ? (
                        <button type="button" className="btn btn--primary" onClick={openProduct3D}>
                            Открыть в 3D
                        </button>
                    ) : null}

                    {!loading && !error && ["product", "warehouse_material"].includes(type) && meta?.canCreateTask ? (
                        <button type="button" className="btn btn--primary" onClick={createTask}>
                            Создать задание
                        </button>
                    ) : null}

                    {!loading && !error && type === "sales_session" && meta?.canUseCalc3d ? (
                        <button type="button" className="btn btn--primary" onClick={open3DSession}>
                            Открыть в 3D
                        </button>
                    ) : null}

                    {!loading && !error && sectionLink ? (
                        <button type="button" className="btn" onClick={openSection}>
                            {type === "warehouse_material" ? "Открыть склад" : "Открыть раздел"}
                        </button>
                    ) : null}

                    <button type="button" className="btn" onClick={close}>
                        Закрыть
                    </button>
                </footer>
            </section>
        </div>,
        portalTarget
    );
}
